import {
  UNMATCHED_DETECTION_TRACK_ID,
  getDateTime,
} from "@/services/buffer-controller/utils";
import {
  DetectionSnapshot,
  LastDetectionsMapping,
  RawDetection,
  RawSnapshot,
  Snapshot,
  SnapshotsGroupedByTrackId,
  TrackID,
} from "@/shared/types";
import { detectionToTrackMap$ } from "@/virtual-store/state";

export const parseLastDetectionsMappingJSON = <T>(json: string | T): T =>
  typeof json === "string" ? JSON.parse(json as string) : json;

export const transformJsonToSnapshots = (
  rawData: RawSnapshot[],
  forEachDetection?: (
    detection: LastDetectionsMapping,
    trackId: TrackID
  ) => void
): SnapshotsGroupedByTrackId[] => {
  const newData = rawData.reduce<Record<string, Snapshot[]>>(
    (acc, snapshot) => {
      const transformedSnapshot: Snapshot = {
        datetime: getDateTime(snapshot),
        pose_type: snapshot.pose_type,
        pose_x: snapshot.pose_x,
        pose_y: snapshot.pose_y,
        pose_z: snapshot.pose_z,
        pose_conf: snapshot.pose_conf,
        last_detections_mapping: snapshot.last_detections_mapping,
        metadata: snapshot,
      };

      const id = "id" in snapshot ? (snapshot.id as string) : snapshot.track_id;

      if (forEachDetection) {
        const lastDetectionsMapping: LastDetectionsMapping[] =
          typeof snapshot.last_detections_mapping === "string"
            ? JSON.parse(String(snapshot.last_detections_mapping))
            : snapshot.last_detections_mapping;

        lastDetectionsMapping.forEach((detection) =>
          forEachDetection(detection, id)
        );
      }

      if (id in acc) {
        acc[id].push(transformedSnapshot);
      } else {
        acc[id] = [transformedSnapshot];
      }

      return acc;
    },
    {}
  );

  const result: SnapshotsGroupedByTrackId[] = Object.entries(newData).map(
    ([id, snapshots]) => ({
      track_id: id,
      snapshots,
    })
  );

  return result;
};

export const transformJsonToDetections = (
  rawData: RawDetection[],
  cameraId: string
): SnapshotsGroupedByTrackId<DetectionSnapshot>[] => {
  const cameraDetections = rawData.filter(
    (detection) => detection.sensor_id == cameraId
  );

  const newData = cameraDetections.reduce<Record<string, DetectionSnapshot[]>>(
    (acc, cameraDetection) => {
      parseLastDetectionsMappingJSON(cameraDetection.objects).forEach(
        (object) => {
          const trackId =
            detectionToTrackMap$[object.id].peek() ||
            UNMATCHED_DETECTION_TRACK_ID;

          const transformedSnapshot: DetectionSnapshot = {
            track_id: trackId,
            message_id: cameraDetection.message_id,
            time: cameraDetection.timestamp,
            sensor_id: cameraDetection.sensor_id,
            object: object,
          };

          if (trackId in acc) {
            acc[trackId].push(transformedSnapshot);
          } else {
            acc[trackId] = [transformedSnapshot];
          }
        },
        []
      );

      return acc;
    },
    {}
  );

  const result: SnapshotsGroupedByTrackId<DetectionSnapshot>[] = Object.entries(
    newData
  ).map(([id, snapshots]) => ({
    track_id: id,
    snapshots,
  }));

  return result;
};

export const getEpoch = (dateTime: Date | number) => {
  return typeof dateTime === "number" ? dateTime : dateTime.getTime();
};

export const calculateSeekValue = (
  seekedDateTime: Date | number,
  videoStartTime: Date
) => {
  return (getEpoch(seekedDateTime) - getEpoch(videoStartTime)) / 1000;
};
